// MAJLIS-66 (UI-C15 ShareRecordSheet, UI-C14 HostControls; D-15, D-16; P-30..P-32; the approved mock-up's frames 3a,
// 3b, 3c and 4a): the host's two sheets inside a finished majlis's record. Both are the app's Sheet (hangs from the
// top, scrim, Escape, focus trap) and both are HOST ONLY - a reader by read link never sees either.
//
// ShareSheet: one line on what a read link is; "New read link" makes one (P-30) and shows its address ONCE, with Copy
// (EM-4: the server keeps only its SHA-256, so it can never be shown again) - the address lives in this component's
// state and is gone when the sheet closes. Below, every link, newest first, with its name (or "Read link"), when it was
// made, and "Turn off" behind its confirmation (P-31); a turned-off link stays, muted, with when. MAJLIS-166: every
// confirmation here is a ConfirmSheet (the question, one red row, Cancel back to this sheet), never an inline block -
// the sheet's component stays mounted while it shows, so a fresh link and a typed name survive a Cancel.
//
// DECIDED HERE (flagged for the consultant): the optional name is typed BEFORE "New read link" and goes in the POST's
// body (ruling 13's wire has no rename route), where frame 3b draws the field beside the fresh link.
//
// OptionsSheet: the host-only section as the seat sheet has it - Share the record (with "Read links: n") and Delete
// record, which opens its ConfirmSheet with one more line than End majlis has (what goes, and that it cannot be
// undone). Nothing is sent until the second tap; a failure keeps the record open and says so.
import { useState } from "react";
import { useI18n } from "../i18n";
import type { ReadLinkInfo } from "../protocol/records";
import { LinkIcon, TrashIcon } from "./icons";
import { Sheet } from "./Sheet";
import { ConfirmSheet } from "./ConfirmSheet";
import { READ_LINK_LABEL_MAX } from "../constants";

function useWhen() {
  const { lang } = useI18n();
  return (iso: string) =>
    new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(
      Date.parse(iso),
    );
}

export function ShareSheet({
  links,
  fresh,
  onCreate,
  onRevoke,
  onClose,
}: {
  /** null while the list is on its way */
  links: ReadLinkInfo[] | null;
  /** the address of the link made in THIS sheet, shown once; null otherwise */
  fresh: string | null;
  onCreate: (label: string | null) => Promise<boolean>;
  onRevoke: (id: string) => Promise<boolean>;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const when = useWhen();
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setBusy(true);
    setFailed(false);
    setCopied(false);
    const ok = await onCreate(label.trim() || null);
    setBusy(false);
    if (ok) setLabel("");
    else setFailed(true);
  };
  const revoke = async (id: string) => {
    setConfirming(null);
    setFailed(!(await onRevoke(id)));
  };

  const asked = confirming !== null ? links?.find((k) => k.id === confirming && !k.revoked) : undefined;
  if (asked) {
    return (
      <ConfirmSheet
        question={t("revokeConfirm")}
        action={t("revoke")}
        icon={<LinkIcon />}
        spec="UI-C15 P-31"
        onConfirm={() => void revoke(asked.id)}
        onCancel={() => setConfirming(null)}
      />
    );
  }
  return (
    <Sheet title={t("share")} onClose={onClose} spec="UI-C15 UI-C14 P-30 P-31 DM-8 D-16">
      <p className="sheet-body">{t("shareBody")}</p>
      <div className="field link-name-field">
        <label htmlFor="link-name">{t("linkName")}</label>
        <input
          id="link-name"
          className="text-input"
          dir="auto"
          autoComplete="off"
          maxLength={READ_LINK_LABEL_MAX}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
      <button
        type="button"
        className="secondary new-link"
        disabled={busy}
        onClick={() => void create()}
        data-spec="UI-C15 P-30 EM-4"
      >
        <LinkIcon />
        {t("newLink")}
      </button>
      {fresh !== null && (
        <div className="link-once" data-spec="EM-4 EM-18 DM-8 P-30">
          <p className="copy-label">{t("linkOnce")}</p>
          <div className="copy-line">
            <code dir="ltr">{fresh}</code>
            <button
              type="button"
              className="text-link"
              onClick={() => {
                void navigator.clipboard?.writeText(fresh).then(() => setCopied(true));
              }}
            >
              {copied ? t("copied") : t("copy")}
            </button>
          </div>
        </div>
      )}
      {failed && (
        <p className="sheet-failed" role="alert">
          {t("actionFailed")}
        </p>
      )}
      {links !== null && links.length > 0 && (
        <ul className="link-list">
          {links.map((k) => {
            const name = k.label ?? t("linkUnnamed");
            return (
              <li key={k.id} className={`link-row${k.revoked ? " revoked" : ""}`} data-link={k.id} data-spec="DM-8 P-30">
                <LinkIcon />
                <span className="link-text">
                  <span className="link-label" dir="auto">
                    {name}
                  </span>
                  <span className="link-meta">
                    {t("linkCreated", { d: when(k.created) })}
                    {k.revoked && ` · ${t("revoked", { d: when(k.revoked) })}`}
                  </span>
                </span>
                {!k.revoked && (
                  <button
                    type="button"
                    className="text-link seat-remove"
                    aria-label={t("revokeNamed", { label: name })}
                    onClick={() => setConfirming(k.id)}
                    data-spec="UI-C15 P-31"
                  >
                    {t("revoke")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
}

export function OptionsSheet({
  live,
  onShare,
  onDelete,
  onClose,
}: {
  /** live read links (null: not known yet) */
  live: number | null;
  onShare: () => void;
  /** resolves true once the server has deleted the record (the app then leaves it); false on a failure */
  onDelete: () => Promise<boolean>;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const del = async () => {
    setBusy(true);
    setFailed(false);
    const ok = await onDelete();
    setBusy(false);
    if (!ok) setFailed(true);
  };
  if (confirming) {
    return (
      <ConfirmSheet
        question={t("deleteConfirm")}
        body={t("deleteWhat")}
        action={t("deleteRecord")}
        icon={<TrashIcon />}
        failed={failed ? t("actionFailed") : undefined}
        busy={busy}
        spec="UI-C14 P-32 D-15 D-13"
        onConfirm={() => void del()}
        onCancel={() => {
          setConfirming(false);
          setFailed(false);
        }}
      />
    );
  }
  return (
    <Sheet title={t("recordMenu")} onClose={onClose} spec="UI-C14 UI-S3 D-15">
      <section className="host-section" data-spec="UI-C14 D-15">
        <h3 className="host-title">{t("recordHostOnly")}</h3>
        <button type="button" className="sheet-row" onClick={onShare} data-spec="UI-C15 P-30">
          <LinkIcon />
          <span>{t("share")}</span>
          {live !== null && live > 0 && <span className="row-note">{t("rowLinks", { n: live })}</span>}
        </button>
        <button
          type="button"
          className="sheet-row delete-row"
          onClick={() => setConfirming(true)}
          data-spec="UI-C14 P-32 D-15"
        >
          <TrashIcon />
          <span>{t("deleteRecord")}</span>
        </button>
      </section>
    </Sheet>
  );
}
